import { useContext, useEffect, useState } from "react";

import { useLocation } from "react-router-dom";
import ls from "local-storage";

import { StoreContext } from "../../store/context";

import TrackingUtil from "../../utils/tracking";
import { hasBoostConnectUrlParams, isLocalDebugMode } from "../../utils";

import { FEATURE_TOGGLE_ENABLE_JOURNEY_EXISTING_FLOW } from "../../constants/journey";
import { EXPERIAN_DISPLAY_TYPE } from "../../constants/experian";
import { TRACKING_PROVIDER } from "../../config/tracking/providers";

import SignUpStepDecider from "./SignUpStepDecider";
// import Template from '../../components/Template';

import "./SignUp.scss";

const SignUp = () => {
  const { state, actions } = useContext(StoreContext);
  const params = new URLSearchParams(useLocation().search);
  const [stepContent, setStepContent] = useState(null);

  useEffect(() => {
    // Skip to Step1PersonalDetails if we did not come from mandate step.
    if (state.prerequisites?.journeyConfigurations?.global?.skipProductPage && !state.kycToken && !state.debug && params.get("step") !== "ScoringFailure" && !state.isOnboardingUpdate) {
      actions.setStep("Step1PersonalDetails");
    }
  }, [state.prerequisites?.journeyConfigurations]);

  useEffect(() => {
    // Handling skipping of steps to avoid loading unwanted component
    let skipped = false;
    switch (state.step) {
      case "Step0ProductPage":
        if (state.prerequisites?.journeyConfigurations?.global.skipProductPage) {
          skipped = true;
          actions.setStep("Step1PersonalDetails");
        }
        break;
      case "Step2OTP":
        if (state.prerequisites?.journeyConfigurations?.global.skipVerifyMsisdnOtp) {
          skipped = true;
          actions.setStep("Step3Password");
        }
        break;
      case "Step3Password":
        if (state.prerequisites?.journeyConfigurations?.global.skipPassword) {
          skipped = true;
          actions.setStep("Step4Address");
        }
        break;
      case "Step4Address":
        if (state.prerequisites?.journeyConfigurations?.global.skipAddress) {
          skipped = true;
          actions.setStep("Step5EmploymentDetails");
        }
        break;
      case "Step5EmploymentDetails":
        if (state.prerequisites?.journeyConfigurations?.global.skipEmploymentDetails) {
          skipped = true;
          if (!state.applicationConfirmed) {
            actions.confirmApplication({ applicationId: state.applicationId }, true);
          } else {
            actions.setStep("Step6KYC");
          }
        }
        break;
      case "Step6KYC":
        if (state.prerequisites?.journeyConfigurations?.global.skipKyc) {
          skipped = true;
          actions.setStep("Step7ShareLocation");
        }
        break;
      case "Step7ShareLocation":
        if (state.prerequisites?.journeyConfigurations?.global.skipShareLocation) {
          skipped = true;
          actions.creditScoring({ applicationId: state.applicationId });
        }
        break;
      default:
        break;
    }
    if (!skipped) {
      const { stepDefinition, jsx } = SignUpStepDecider(state.step, state.prerequisites);
      actions.setData({
        stepDefinition,
      });
      setStepContent(jsx);
    }
  }, [state.step, state.prerequisites]);

  useEffect(() => {
    // validate missing lToken when prerequisites.skipPassword = true
    const deepLinkUrl = params.get("deepLinkUrl");
    if (deepLinkUrl && deepLinkUrl.length > 0) {
      window.location.href = deepLinkUrl;
    }
  }, [params.get("deepLinkUrl")]);

  useEffect(() => {
    const clientId = params.get("clientId");
    const isOnboardingUpdate = params.get("update") === "true";
    const externalCustomerId = params.get("externalCustomerId");

    actions.setData({
      currentPage: "signUp",
    });

    // Removes storage
    sessionStorage.removeItem("clientId");

    if (clientId) {
      sessionStorage.setItem("clientId", clientId);
    }

    // dev - quick jump to component
    if (!state.prerequisites && !hasBoostConnectUrlParams(params) && isLocalDebugMode()) {
      actions.prerequisites({
        clientId,
      });
      actions.setData({
        debug: params.get("debug"),
        lToken: params.get("lToken"),
        // pToken: params.get("pToken"),
        piiData: {
          // Use empty object to simulate no prefill data (user fills manually):
          // "additionalDetails": {},
          "additionalDetails": {
            "maritalStatus": "Single",
            "race": "Malay",
            "bumiputeraStatus": "BUMIPUTERA",
            "employerName": "test",
            "epfContribution": "MANDATORY_ONLY"
          },
          "annualIncomeRange": {
            "end": 100000,
            "start": 50000
          },
          "businessCategory": {
            "categoryId": 5000,
            "subCategoryId": 0
          },
          "clientId": "8888",
          "fullName": "Some Full Name",
          "nric": "880808088888",
          "email": "example@myboost.co",
          "msisdn": "60123456789",
          "employmentStatusId": "1004",
          "homeAddress": {
            "addressLine1": "Block A, Jalan Satu",
            "addressLine2": "Taman Dua",
            "addressLine3": "",
            "city": "petaling jaya",
            "postcode": "47600",
            "state": "selangor"
          },
          "idNumber": "880808088888",
          "idType": "NRIC",
          "natureOfBusiness": "Food & Beverage",
          "occupation": "Waiter / Waitress",
          "occupationTypeId": "1002",
          "uid": "63db7d33e95bcb3e697902d8"
        },
        embedded: params.get("embedded") === "true",
        clientId,
        applicationId: "2504237457973503", // params.get("applicationId"),
        customerId: "2410233472993407", // params.get("customerId"),
        step: params.get("step"),
        isBoostJourney: true,
        isOnboardingUpdate: isOnboardingUpdate,
        externalCustomerId: externalCustomerId,
        experian: {
          "downtimeFlag": true,
          "startTime": "13:00", // it wont return if downtimeFlag is false
          "endTime": "14:00", // it wont return if downtimeFlag is false
          displayType: EXPERIAN_DISPLAY_TYPE.BANNER,
        }
      });
    }
    // if (params.get("testCamera") === "1") {
    //   actions.setData({
    //     pToken: params.get("pToken"),
    //     applicationId: params.get("applicationId"),
    //     customerId: params.get("customerId"),
    //     lToken: params.get("lToken"),
    //     embedded: params.get("embedded") === "true",
    //     step: "Step6KYC", // Step6KYC Step0ProductPage
    //   });
    // }
    else if (clientId && params.get("step") == "ScoringFailure") {
      actions.setData({
        clientId,
        applicationId: params.get("applicationId"),
        customerId: params.get("customerId"),
        embedded: params.get("embedded") === "true",
        step: "ScoringFailure",
      });
    } else if (clientId && params.get("step") !== "8") {
      if (!state.prerequisites) {
        actions.prerequisites({
          clientId,
        });
      }
      // create account with valid URL
      actions.setData({
        clientId,
        pToken: params.get("pToken"),
        applicationId: params.get("applicationId"),
        customerId: params.get("customerId"),
        lToken: params.get("lToken"),
        embedded: params.get("embedded") === "true",
        isOnboardingUpdate: isOnboardingUpdate,
        externalCustomerId: externalCustomerId,
        ...(FEATURE_TOGGLE_ENABLE_JOURNEY_EXISTING_FLOW
          ? { step: "Step0ProductPage" }
          : { step: isOnboardingUpdate ? "PartnershipConfirmation" : "Step0ProductPage", }
        )
      });
    } else if (params.get("step") === "8" || hasBoostConnectUrlParams(params)) {
      // after mandate come back and redirect them to desire page and check mandate status
      actions.setData({ ...ls.get("bnplstate") });
    } else {
      actions.setStep(null);
      actions.showInvalidUrlDialog();
    }
  }, []);

  useEffect(() => {
    const externalCustomerId = params.get("externalCustomerId");

    // TrackingUtil.clearNetcoreUserHelper();

    if (externalCustomerId && externalCustomerId.length > 0) {
      const cancelPolling = TrackingUtil.identifyUserForProviderWhenReady(
        TRACKING_PROVIDER.NETCORE,
        externalCustomerId,
        { maxAttempts: 20, intervalMs: 500 }
      );
      return cancelPolling;
    }
  }, []);

  return (
    <>
      {/* <Template paylaterLogo={true} footer={true}> */}
      {stepContent}
      {/* </Template> */}
    </>
  );
};

export default SignUp;
